import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import authService from '../../services/authService';
import salesforceService from '../../services/salesforceService';
import profileService from '../../services/profileService';

import AuthenticationTabs from './components/AuthenticationTabs';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import ForgotPasswordModal from './components/ForgotPasswordModal';
import TrustSignals from './components/TrustSignals';

const PartnerAuthentication = () => {
  const navigate = useNavigate();
  const { signIn, signInWithGoogle } = useAuth();
  const [activeTab, setActiveTab] = useState('login');
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });

  const [registerData, setRegisterData] = useState({
    companyName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
  });

  const [loginErrors, setLoginErrors] = useState({});
  const [registerErrors, setRegisterErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  const handleLoginChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setLoginData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (loginErrors?.[name]) {
      setLoginErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleRegisterChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setRegisterData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (registerErrors?.[name]) {
      setRegisterErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateLogin = () => {
    const errors = {};
    
    if (!loginData?.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(loginData?.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!loginData?.password) {
      errors.password = 'Password is required';
    } else if (loginData?.password?.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    setLoginErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const validateRegister = () => {
    const errors = {};
    
    if (!registerData?.companyName?.trim()) {
      errors.companyName = 'Company name is required';
    }
    
    if (!registerData?.firstName?.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!registerData?.lastName?.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!registerData?.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(registerData?.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!registerData?.phone?.trim()) {
      errors.phone = 'Phone number is required';
    }
    
    if (!registerData?.password) {
      errors.password = 'Password is required';
    } else if (registerData?.password?.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    
    if (registerData?.password !== registerData?.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    if (!registerData?.agreeToTerms) {
      errors.agreeToTerms = 'You must agree to the Terms of Service and Privacy Policy';
    }

    setRegisterErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleLoginSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateLogin()) return;

    setIsLoading(true);
    setLoginErrors({});
    
    try {
      // Use AuthContext signIn which calls authService
      const { data, error } = await signIn(loginData?.email, loginData?.password);

      if (error) {
        setLoginErrors({
          email: error?.message || 'Invalid credentials. Please try again.'
        });
        return;
      }

      if (data?.user) {
        // Fetch user profile to check role - FIX: Handle response correctly
        const profile = await profileService?.getUserProfile();
        
        // Check if profile has role property directly (not nested in data)
        if (profile?.role === 'admin') {
          // Admin users go to admin partner management
          navigate('/admin-partner-management');
        } else {
          // Partner users go to partner dashboard
          navigate('/partner-dashboard');
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      setLoginErrors({
        email: 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateRegister()) return;

    setIsLoading(true);
    setRegisterErrors({});
    setSuccessMessage('');
    
    try {
      const fullName = `${registerData?.firstName} ${registerData?.lastName}`;
      
      const { data, error, message } = await authService?.signUp(
        registerData?.email,
        registerData?.password,
        fullName,
        registerData?.companyName,
        registerData?.phone
      );

      if (error) {
        setRegisterErrors({
          email: error?.message || 'Failed to create account. Please try again.'
        });
        return;
      }

      // NEW: Create Account and Contact in Salesforce after successful registration
      if (data?.user?.id) {
        try {
          console.log('Creating Salesforce Account and Contact...');
          const salesforceResult = await salesforceService?.createPartnerAccountAndContact({
            companyName: registerData?.companyName,
            firstName: registerData?.firstName,
            lastName: registerData?.lastName,
            email: registerData?.email,
            phone: registerData?.phone,
            userId: data?.user?.id
          });

          if (salesforceResult?.success) {
            console.log('✅ Salesforce Account ID:', salesforceResult?.accountId);
            console.log('✅ Salesforce Contact ID:', salesforceResult?.contactId);
          }
        } catch (salesforceError) {
          // Log Salesforce error but don't fail registration
          console.error('Salesforce sync failed (non-critical):', salesforceError?.message);
        }
      }

      // Show success message
      setSuccessMessage(message || 'Account created! Please check your email to verify your account.');
      
      // Switch to login tab after 2 seconds
      setTimeout(() => {
        setActiveTab('login');
        setSuccessMessage('');
        setRegisterData({
          companyName: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          password: '',
          confirmPassword: '',
          agreeToTerms: false,
        });
      }, 3000);
      
    } catch (err) {
      console.error('Registration error:', err);
      setRegisterErrors({
        email: 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await signInWithGoogle();
      
      if (error) {
        // Show error message to user
        const errorMsg = error?.message || 'Failed to authenticate with Google. Please try again.';
        if (activeTab === 'login') {
          setLoginErrors({ email: errorMsg });
        } else {
          setRegisterErrors({ email: errorMsg });
        }
        return;
      }

      // OAuth will redirect automatically, but we handle any errors here
      if (data?.url) {
        // User will be redirected to Google login
        // After successful auth, they'll be redirected back to /partner-dashboard
        console.log('Redirecting to Google OAuth...');
      }
    } catch (err) {
      console.error('Google auth error:', err);
      const errorMsg = 'An unexpected error occurred. Please try again.';
      if (activeTab === 'login') {
        setLoginErrors({ email: errorMsg });
      } else {
        setRegisterErrors({ email: errorMsg });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-[260px] h-28 mb-2">
            <img 
              src="/assets/images/Blue_Logo_-_Full_Name-1765020226497.png" 
              alt="Clear Environmental Services logo - horizontal layout with blue water droplet icon featuring flowing wave patterns on left, company name 'clear ENVIRONMENTAL SERVICES' with bold navy blue and gray text on right" 
              className="w-[240px] h-[120px] object-contain"
            />
          </div>
          <h1 className="text-3xl font-semibold text-foreground mb-2">Clear Partner Portal</h1>
          <p className="text-muted-foreground">
            {activeTab === 'login' ? 'Sign in to manage your referral leads' : 'Join our partner network today'}
          </p>
        </div>

        {successMessage && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 text-sm text-center">{successMessage}</p>
          </div>
        )}

        <div className="bg-card rounded-lg border border-border elevation-2 overflow-hidden">
          <AuthenticationTabs activeTab={activeTab} onTabChange={setActiveTab} />
          
          <div className="p-6 sm:p-8">
            {activeTab === 'login' ? (
              <>
                <LoginForm
                  formData={loginData}
                  errors={loginErrors}
                  isLoading={isLoading}
                  onInputChange={handleLoginChange}
                  onSubmit={handleLoginSubmit}
                  onForgotPassword={() => setIsForgotPasswordOpen(true)}
                  onGoogleAuth={handleGoogleAuth}
                />
              </>
            ) : (
              <RegisterForm
                formData={registerData}
                errors={registerErrors}
                isLoading={isLoading}
                onInputChange={handleRegisterChange}
                onSubmit={handleRegisterSubmit}
                onGoogleAuth={handleGoogleAuth}
              />
            )}

            <TrustSignals />
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6 px-4">
          By continuing, you agree to Clear's Terms of Service and Privacy Policy. Your data is protected with industry-standard encryption.
        </p>
      </div>

      <ForgotPasswordModal 
        isOpen={isForgotPasswordOpen} 
        onClose={() => setIsForgotPasswordOpen(false)} 
      />
    </div>
  );
};

export default PartnerAuthentication;